import Konva from 'konva'
import {
  MIN_SIZE,
  TEXT_FONT_FAMILY,
  TEXT_LINE_HEIGHT,
  TEXT_PADDING_X,
  TEXT_PADDING_Y,
} from '../constants'
import { TextLink } from './textLinks'

export const TEXT_CONTENT_NAME = 'text-content'

export type TextRun = {
  text: string
  x: number
  y: number
  width: number
  link?: TextLink
}

let measurer: Konva.Text | null = null

const measureText = (
  text: string,
  contentWidthPx: number,
  fontSizePx: number,
) => {
  if (!measurer) measurer = new Konva.Text({ listening: false })
  measurer.setAttrs({
    text,
    width: Math.max(1, contentWidthPx),
    fontSize: fontSizePx,
    fontFamily: TEXT_FONT_FAMILY,
    lineHeight: TEXT_LINE_HEIGHT,
    align: 'center',
    wrap: 'word',
    padding: 0,
  })
  return measurer
}

export const measureTextBoxHeightPx = (
  text: string,
  boxWidthPx: number,
  fontSizePx: number,
) => {
  const node = measureText(
    text || 'Text',
    boxWidthPx - TEXT_PADDING_X * 2,
    fontSizePx,
  )
  const contentHeight = Math.max(node.height(), fontSizePx * TEXT_LINE_HEIGHT)
  return contentHeight + TEXT_PADDING_Y * 2
}

export const layoutTextRuns = (
  text: string,
  links: TextLink[],
  contentWidthPx: number,
  fontSizePx: number,
): TextRun[] => {
  const node = measureText(text, contentWidthPx, fontSizePx)
  const lineHeightPx = fontSizePx * TEXT_LINE_HEIGHT
  const widthOf = (value: string) => node.measureSize(value).width
  let cursor = 0

  return node.textArr.flatMap((line, lineIndex) => {
    const lineStart = Math.max(cursor, text.indexOf(line.text, cursor))
    const lineEnd = lineStart + line.text.length
    cursor = lineEnd

    const pieces: { start: number; end: number; link?: TextLink }[] = []
    let position = lineStart
    links
      .filter((link) => link.end > lineStart && link.start < lineEnd)
      .forEach((link) => {
        const start = Math.max(link.start, lineStart)
        const end = Math.min(link.end, lineEnd)
        if (start > position) pieces.push({ start: position, end: start })
        pieces.push({ start, end, link })
        position = end
      })
    if (position < lineEnd) pieces.push({ start: position, end: lineEnd })

    const lineX = (node.width() - line.width) / 2
    return pieces.map(({ start, end, link }) => ({
      text: text.slice(start, end),
      x: lineX + widthOf(text.slice(lineStart, start)),
      y: lineIndex * lineHeightPx,
      width: widthOf(text.slice(start, end)),
      link,
    }))
  })
}

export const reflowTextGroup = (group: Konva.Group) => {
  const scaleX = group.scaleX()
  const nextWidth = Math.max(MIN_SIZE, group.width() * Math.abs(scaleX))
  group.scaleX(1)
  group.scaleY(1)
  group.width(nextWidth)

  const content = group.findOne(`.${TEXT_CONTENT_NAME}`)
  const rect = group.findOne('Rect') as Konva.Rect | undefined
  if (content) content.width(Math.max(1, nextWidth - TEXT_PADDING_X * 2))

  const nextHeight = content
    ? measureTextBoxHeightPx(
        content.getAttr('text'),
        nextWidth,
        content.getAttr('fontSize'),
      )
    : TEXT_PADDING_Y * 2

  group.height(nextHeight)
  group.offsetX(nextWidth / 2)
  group.offsetY(nextHeight / 2)
  group.clipX(0)
  group.clipY(0)
  group.clipWidth(nextWidth)
  group.clipHeight(nextHeight)

  rect?.width(nextWidth)
  rect?.height(nextHeight)
}
